import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Animated,
    Easing,
    Image,
    type ImageSourcePropType,
    Platform,
    Pressable,
    StyleSheet,
    View,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';

const DESKTOP_BREAKPOINT = 768;
const HORIZONTAL_ITEM_WIDTH = 172;
const HORIZONTAL_GAP = 20;
const HORIZONTAL_DURATION_MS = 30000;
const MOBILE_SWAP_MS = 2800;
const MOBILE_TRANSITION_MS = 320;
const TICKER_BG = '#f7f9fc';
const TICKER_BG_TRANSPARENT = 'rgba(247,249,252,0)';

export type LogoTickerItem = {
    key: string;
    label: string;
    source: ImageSourcePropType;
    compact?: boolean;
    tall?: boolean;
};

type LogoTickerProps = {
    logos: LogoTickerItem[];
};

function LogoTicker({
    logos,
}: LogoTickerProps) {
    const { width } = useHydratedWindowDimensions();
    const isDesktop = width >= DESKTOP_BREAKPOINT;

    if (logos.length === 0) {
        return null;
    }

    return (
        <View style={styles.container}>
            <View style={styles.tickerContainer}>
                {isDesktop ? (
                    <HorizontalTicker logos={logos} />
                ) : (
                    <VerticalTicker logos={logos} />
                )}
            </View>
        </View>
    );
}

export default React.memo(LogoTicker);

function HorizontalTicker({ logos }: { logos: LogoTickerItem[] }) {
    const { i18n } = useLingui();
    const [isPaused, setIsPaused] = useState(false);
    const translateX = useRef(new Animated.Value(0)).current;
    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    const marqueeLogos = useMemo(() => [...logos, ...logos, ...logos], [logos]);
    const totalWidth = useMemo(
        () => (HORIZONTAL_ITEM_WIDTH + HORIZONTAL_GAP) * logos.length,
        [logos.length]
    );

    const startAnimation = useCallback((fromValue: number) => {
        const normalizedStart = Math.max(-totalWidth, Math.min(0, fromValue));
        const progress = Math.abs(normalizedStart) / totalWidth;
        const remaining = Math.max(0.05, 1 - progress);

        animationRef.current?.stop();
        animationRef.current = Animated.timing(translateX, {
            toValue: -totalWidth,
            duration: Math.round(HORIZONTAL_DURATION_MS * remaining),
            easing: Easing.linear,
            useNativeDriver: Platform.OS !== 'web',
        });

        animationRef.current.start(({ finished }) => {
            if (!finished) {
                return;
            }
            translateX.setValue(0);
            startAnimation(0);
        });
    }, [totalWidth, translateX]);

    useEffect(() => {
        if (isPaused) {
            animationRef.current?.stop();
            return;
        }

        let isActive = true;
        translateX.stopAnimation((value) => {
            if (!isActive) {
                return;
            }
            startAnimation(value);
        });

        return () => {
            isActive = false;
            animationRef.current?.stop();
        };
    }, [isPaused, startAnimation, translateX]);

    const tickerItems = marqueeLogos.map((logo, index) => (
        <View
            key={`${logo.key}-${index}`}
            style={[
                styles.horizontalItem,
                { width: HORIZONTAL_ITEM_WIDTH, marginRight: HORIZONTAL_GAP },
            ]}
        >
            <View style={styles.logoSurface}>
                <Image
                    source={logo.source}
                    style={[
                        styles.horizontalLogo,
                        logo.compact && styles.horizontalLogoCompact,
                        logo.tall && styles.horizontalLogoTall,
                    ]}
                    resizeMode='contain'
                    accessibilityLabel={i18n._({ ...msg({ id: 'marketing.customerLogoLabel', message: '{brand} logo' }), values: { brand: logo.label } })}
                />
            </View>
        </View>
    ));

    return (
        <View style={styles.horizontalMask}>
            <Pressable
                style={styles.horizontalPressable}
                onPressIn={() => setIsPaused(true)}
                onPressOut={() => setIsPaused(false)}
                onHoverIn={() => setIsPaused(true)}
                onHoverOut={() => setIsPaused(false)}
            >
                <Animated.View style={[styles.horizontalTrack, { transform: [{ translateX }] }]}>
                    {tickerItems}
                </Animated.View>
            </Pressable>

            <LinearGradient
                colors={[TICKER_BG, TICKER_BG_TRANSPARENT]}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={[styles.gradientMask, styles.leftMask]}
                pointerEvents='none'
            />
            <LinearGradient
                colors={[TICKER_BG_TRANSPARENT, TICKER_BG]}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={[styles.gradientMask, styles.rightMask]}
                pointerEvents='none'
            />
        </View>
    );
}

function VerticalTicker({ logos }: { logos: LogoTickerItem[] }) {
    const { i18n } = useLingui();
    const [index, setIndex] = useState(0);
    const fadeAnim = useRef(new Animated.Value(1)).current;
    const slideAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const interval = setInterval(() => {
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 0,
                    duration: MOBILE_TRANSITION_MS,
                    useNativeDriver: Platform.OS !== 'web',
                }),
                Animated.timing(slideAnim, {
                    toValue: -18,
                    duration: MOBILE_TRANSITION_MS,
                    useNativeDriver: Platform.OS !== 'web',
                }),
            ]).start(() => {
                setIndex((prev) => (prev + 1) % logos.length);
                slideAnim.setValue(18);

                Animated.parallel([
                    Animated.timing(fadeAnim, {
                        toValue: 1,
                        duration: MOBILE_TRANSITION_MS,
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                    Animated.timing(slideAnim, {
                        toValue: 0,
                        duration: MOBILE_TRANSITION_MS,
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                ]).start();
            });
        }, MOBILE_SWAP_MS);

        return () => clearInterval(interval);
    }, [fadeAnim, logos.length, slideAnim]);

    const current = logos[index];

    return (
        <View style={styles.verticalContainer}>
            <Animated.View
                style={[
                    styles.verticalItem,
                    {
                        opacity: fadeAnim,
                        transform: [{ translateY: slideAnim }],
                    },
                ]}
            >
                <View style={styles.verticalLogoSurface}>
                    <Image
                        source={current.source}
                        style={[
                            styles.verticalLogo,
                            current.compact && styles.verticalLogoCompact,
                            current.tall && styles.verticalLogoTall,
                        ]}
                        resizeMode='contain'
                        accessibilityLabel={i18n._({ ...msg({ id: 'marketing.customerLogoLabel', message: '{brand} logo' }), values: { brand: current.label } })}
                    />
                </View>
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: TICKER_BG,
        borderWidth: 1,
        borderColor: '#e8edf3',
        borderRadius: 18,
        paddingVertical: 14,
        marginBottom: 48,
        overflow: 'hidden',
    },
    tickerContainer: {
        minHeight: 84,
        justifyContent: 'center',
    },
    horizontalMask: {
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
    },
    horizontalPressable: {
        width: '100%',
    },
    horizontalTrack: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
    },
    horizontalItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    logoSurface: {
        width: '100%',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#e3e8ee',
        backgroundColor: '#ffffff',
        minHeight: 66,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        overflow: 'hidden',
        ...(Platform.OS !== 'web' && {
            shadowColor: '#111827',
            shadowOpacity: 0.04,
            shadowRadius: 10,
            shadowOffset: { width: 0, height: 3 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 3px 10px rgba(17,24,39,0.06)',
        }),
    },
    horizontalLogo: {
        width: 108,
        height: 38,
    },
    horizontalLogoCompact: {
        width: 128,
    },
    horizontalLogoTall: {
        width: 90,
        height: 44,
    },
    gradientMask: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 104,
    },
    leftMask: {
        left: 0,
    },
    rightMask: {
        right: 0,
    },
    verticalContainer: {
        height: 94,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
    },
    verticalItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 22,
    },
    verticalLogoSurface: {
        width: 220,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#e3e8ee',
        backgroundColor: '#ffffff',
        minHeight: 70,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
        paddingVertical: 12,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#111827',
            shadowOpacity: 0.05,
            shadowRadius: 12,
            shadowOffset: { width: 0, height: 4 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 4px 12px rgba(17,24,39,0.08)',
        }),
    },
    verticalLogo: {
        width: 126,
        height: 40,
    },
    verticalLogoCompact: {
        width: 152,
    },
    verticalLogoTall: {
        width: 104,
        height: 48,
    },
});
